import type { Responsive } from '#types/common'
import type { Directive, DirectiveBinding, VNode } from 'vue'
import { normalizeClass, normalizeStyle } from 'vue'
import { getStyleProps } from './getStyleProps'

export const styleProps = [
  'aspect-ratio',
  'b',
  'bg',
  'bgi',
  'bgv',
  'c',
  'display',
  'gap',
  'gap-x',
  'gap-y',
  'grid-area',
  'grid-col',
  'cols',
  'grid-row',
  'rows',
  'h',
  'inset',
  'lh',
  'max-h',
  'max-w',
  'min-h',
  'min-w',
  'm',
  'mb',
  'mt',
  'ml',
  'mr',
  'mx',
  'my',
  'object-position',
  'p',
  'pb',
  'pr',
  'pt',
  'px',
  'py',
  'row-start',
  'text-size',
  'top',
  'w',
] as const

export type StyleProp = typeof styleProps[number]
export type ResponsiveStyles = Record<StyleProp, Responsive>

export const getStyle = (config: ResponsiveStyles, arg?: string): { classes: string, style: Record<string, string> } => {
  const styleConfig = arg ? { [arg]: config } : config

  const styles = Object.entries(styleConfig).reduce((acc, [prop, value]) => {
    if (!value || (isObject(value) && Object.keys(value).length === 0)) return acc

    const { styles, classList } = getStyleProps(prop as StyleProp, value)

    return {
      classes: [...acc.classes, ...classList!],
      style: { ...acc.style, ...styles }
    }
  }, {
    classes: [],
    style: {}
  } as any)

  return { ...styles, classes: styles.classes.join(' ') }
}

const BASE = Symbol('vStyleBase')

type InjectedProps = Record<string, any> & { [BASE]?: { class: unknown, style: unknown } }

/** Tags the exported `vStyle` directive so `activeBindings` can recognize sibling `v-style` bindings on a vnode by marker instead of by object identity, avoiding a forward-reference to `vStyle` itself. */
const MARKER = Symbol('vStyle')

type MarkedDirective = { [MARKER]?: true }

/**
 * A single-root component can receive a `v-style` from its usage site as well as define its own on
 * the root element - Vue then merges both bindings onto that one rendered vnode (`vnode.dirs`). Each
 * binding's hook still fires independently, so `inject` has to recompute the *combined* set of all
 * `v-style` bindings living on the vnode rather than only the one that triggered it, or whichever
 * binding runs last would overwrite the `class`/`style` the other had already written into props.
 * Falls back to just the triggering binding when `vnode.dirs` isn't populated (e.g. a directive
 * invoked directly in a unit test, where there is only ever one binding to begin with).
 */
const activeBindings = (
  vnode: VNode<any, any> | null | undefined,
  binding: DirectiveBinding<ResponsiveStyles>
): DirectiveBinding<ResponsiveStyles>[] => {
  const siblings = (vnode?.dirs as DirectiveBinding<ResponsiveStyles>[] | undefined)
    ?.filter((dir) => (dir.dir as MarkedDirective)?.[MARKER])

  return siblings?.length ? siblings : [binding]
}

/** Combines every active binding's generated classes/styles into a single set, later ones winning on conflicting style keys. */
const combine = (bindings: DirectiveBinding<ResponsiveStyles>[]) =>
  bindings.reduce((acc, { arg, value }) => {
    if (!value) return acc

    const { classes, style } = getStyle(value, arg)

    return {
      classes: [acc.classes, classes].filter(Boolean).join(' '),
      style: { ...acc.style, ...style }
    }
  }, { classes: '', style: {} as Record<string, string> })

/**
 * Writes the generated classes and CSS variables into the vnode props, so Vue owns them and its own
 * `class`/`style` patching carries them along instead of overwriting them.
 *
 * Vue reads `vnode.props` before invoking us, so the object has to be edited in place - and the
 * compiler hoists a fully static one and reuses it for every render and every element. The pristine
 * `class`/`style` is therefore kept aside on first use and every write starts from it, so repeated
 * injections stay idempotent and one element cannot inherit another's classes.
 */
const inject = (vnode: VNode<any, any>, binding: DirectiveBinding<ResponsiveStyles>) => {
  const props: InjectedProps | null = vnode?.props ?? null
  if (!props) return

  const { classes, style } = combine(activeBindings(vnode, binding))
  const base = props[BASE] ??= { class: props.class, style: props.style }

  props.class = normalizeClass([base.class, classes])
  props.style = { ...normalizeStyle([base.style]) as object, ...style }
}

/**
 * Applies the vnode-wide *combined* set of classes/variables straight to the DOM node, clearing
 * whatever the triggering binding's own previous value contributed. Still needed next to `inject`
 * because Vue only patches `class`/`style` from props when the compiler marked them dynamic - for
 * `<div class="static" v-style:c="x" />` it never does, so a changed value would otherwise leave
 * stale classes and variables behind.
 *
 * Recomputes from every active binding (`combine`/`activeBindings`) rather than only the binding that
 * triggered the hook - otherwise, when two `v-style` bindings share one element and set the same key,
 * the one whose own value happens to change later would blindly overwrite the other's on the DOM,
 * even though `combine` had already resolved who should win. The "old" combined set used to work out
 * what to remove is derived the same way, swapping the triggering binding's current value back out
 * for its `oldValue` - no extra state has to be tracked on the element itself.
 */
const render = (
  el: HTMLElement,
  vnode: VNode<any, any> | null | undefined,
  binding: DirectiveBinding<ResponsiveStyles>
) => {
  const bindings = activeBindings(vnode, binding)
  const { classes: classString, style } = combine(bindings)
  const classes = classString ? classString.split(' ') : []

  if (binding.oldValue) {
    const oldValue = binding.oldValue
    const oldBindings = bindings.map((sibling) =>
      sibling === binding ? { ...binding, value: oldValue } : sibling)
    const { classes: oldClassString, style: oldStyle } = combine(oldBindings)
    const oldClasses = oldClassString ? oldClassString.split(' ') : []

    oldClasses.filter((name) => !classes.includes(name)).forEach((name) => el.classList.remove(name))
    Object.keys(oldStyle).filter((key) => !(key in style)).forEach((key) => el.style.removeProperty(key))
  }

  classes.forEach((name) => el.classList.add(name))
  Object.entries(style).forEach(([key, val]) => el.style.setProperty(key, val))
}

const updated = (
  el: HTMLElement,
  binding: DirectiveBinding<ResponsiveStyles>,
  vnode: VNode<any, any> | null | undefined
) => {
  // A changed value is what requires recomputing the combined set - an unrelated vnode patch doesn't
  if (JSON.stringify(binding.value) === JSON.stringify(binding.oldValue)) return

  render(el, vnode, binding)
}

/**
 * Directive: v-style
 * @description
 * Generates UnoCSS classes and CSS variables based on responsive color config
 * It allows to automatically bind text, background color or class on certain resolution
 * @example
 * If classes supported by auto-infer
 * "titleColor": { "sm": "#656565e0", "md": "red", "lg": "blue" }
 * "titleBackground": { "sm": "#656565e0", "md": "red", "lg": "green" }
 * Multiple props: <div v-style="{ c: titleColor, bg: titleBackground }" />
 * Single prop: <div v-style:c="titleColor" />
 * If class has no support by auto-infer
 * "hiddenOnScreen": { "sm": true, "md": true }
 * Usage: <div v-style:hidden="hiddenOnScreen" />
 *
 * A single-root component may define its own `v-style` on its root element while its usage site
 * also applies one - Vue merges both onto the same rendered vnode, and this directive composes
 * their classes/styles together instead of the later one overwriting the former.
 */
export const vStyle: Directive<HTMLElement> & MarkedDirective = {
  created: (el, binding, node) => inject(node, binding),
  beforeMount: (el, binding, node) => render(el, node, binding),
  beforeUpdate: (el, binding, node) => inject(node, binding),
  updated,
  getSSRProps({ arg, value }: DirectiveBinding<ResponsiveStyles>) {
    if (!value) return
    const { classes, style } = getStyle(value, arg)

    return {
      class: classes,
      style
    }
  },
  [MARKER]: true
}
